import 'ckeditor5/ckeditor5.css';
import { Plugin, ButtonView, Command } from "ckeditor5";

import {
  ClassicEditor,

  Essentials,
  Paragraph,
  Heading,

  Bold,
  Italic,
  Underline,
  Strikethrough,
  Subscript,
  Superscript,
  Code,

  Font,
  FontFamily,
  FontSize,
  FontColor,
  FontBackgroundColor,

  Alignment,

  List,
  ListProperties,

  Indent,
  IndentBlock,

  Link,

  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,

  Image,
  ImageToolbar,
  ImageCaption,
  ImageStyle,
  ImageResize,
  ImageInsert,
  ImageInsertViaUrl,
  ImageResizeEditing,
  ImageResizeHandles,

  MediaEmbed,

  BlockQuote,

  HorizontalLine,

  SpecialCharacters,
  SpecialCharactersEssentials,

  FindAndReplace,

  RemoveFormat,

  Undo,

  SourceEditing,

  GeneralHtmlSupport,

  PageBreak,

  ShowBlocks,

  SelectAll,

  PasteFromOffice,
} from "ckeditor5";


// ======================================================
// EMPTY LINE COMMAND
// ======================================================
class EmptyLineCommand extends Command {
  execute() {
    const editor = this.editor;

    editor.model.change((writer) => {
      const selection = editor.model.document.selection;
      const position = selection.getFirstPosition();

      const emptyLine = writer.createElement("emptyLine");

      // CKEditor will auto split paragraph if cursor is in middle
      editor.model.insertContent(emptyLine, position);

      // move cursor after emptyLine
      writer.setSelection(writer.createPositionAfter(emptyLine));
    });

    editor.editing.view.focus();
  }

  refresh() {
    this.isEnabled = true;
  }
}

// ======================================================
// EMPTY LINE PLUGIN
// ======================================================
class EmptyLinePlugin extends Plugin {
  init() {
    const editor = this.editor;

    // -------------------------------
    // One key Backspace/Delete support for emptyLine
    // -------------------------------
    editor.keystrokes.set('Backspace', (data, cancel) => {
      const model = editor.model;
      const selection = model.document.selection;
      const position = selection.getFirstPosition();

      model.change((writer) => {
        const nodeBefore = position.nodeBefore;

        if (nodeBefore && nodeBefore.name === 'emptyLine') {
          writer.remove(nodeBefore);
          cancel();
        }
      });
    });

    editor.keystrokes.set('Delete', (data, cancel) => {
      const model = editor.model;
      const selection = model.document.selection;
      const position = selection.getFirstPosition();

      model.change((writer) => {
        const nodeAfter = position.nodeAfter;

        if (nodeAfter && nodeAfter.name === 'emptyLine') {
          writer.remove(nodeAfter);
          cancel();
        }
      });
    });

    // -------------------------------
    // Model Schema
    // -------------------------------
    editor.model.schema.register("emptyLine", {
      allowWhere: "$block",
      isBlock: true,
      isObject: true
    });

    // -------------------------------
    // Upcast HTML -> Model
    // -------------------------------
    editor.conversion.for("upcast").elementToElement({
      view: {
        name: "div",
        attributes: {
          "data-empty-line": "true"
        }
      },
      model: "emptyLine"
    });

    // -------------------------------
    // Data Downcast Model -> HTML saved in editor.getData()
    // -------------------------------
    editor.conversion.for("dataDowncast").elementToElement({
      model: "emptyLine",
      view: (modelElement, { writer }) => {
        return writer.createEmptyElement("div", {
          "data-empty-line": "true"
        });
      }
    });

    // -------------------------------
    // Editing Downcast Model -> visual blank line
    // -------------------------------
    editor.conversion.for("editingDowncast").elementToElement({
      model: "emptyLine",
      view: (modelElement, { writer }) => {
        const div = writer.createContainerElement("div", {
          class: "empty-line-token"
        });

        return div;
      }
    });

    // -------------------------------
    // Register Command
    // -------------------------------
    editor.commands.add("emptyLine", new EmptyLineCommand(editor));

    // -------------------------------
    // Toolbar Button
    // -------------------------------
    editor.ui.componentFactory.add("emptyLine", (locale) => {
      const button = new ButtonView(locale);

      button.set({
        label: "Next Line",
        tooltip: true,
        withText: true
      });

      button.on("execute", () => {
        editor.execute("emptyLine");
      });

      return button;
    });
  }
}


// ======================================================
// CUSTOM CLASSIC EDITOR
// ======================================================
class CustomClassicEditor extends ClassicEditor { }

CustomClassicEditor.builtinPlugins = [

  EmptyLinePlugin,

  Essentials,
  Paragraph,
  Heading,

  Font,

  Bold,
  Italic,
  Underline,
  Strikethrough,
  Subscript,
  Superscript,
  Code,

  FontFamily,
  FontSize,
  FontColor,
  FontBackgroundColor,

  Alignment,

  List,
  ListProperties,

  Indent,
  IndentBlock,

  Link,

  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,

  Image,
  ImageToolbar,
  ImageCaption,
  ImageStyle,
  ImageResize,
  ImageInsert,
  ImageInsertViaUrl,

  MediaEmbed,

  BlockQuote,

  HorizontalLine,

  SpecialCharacters,
  SpecialCharactersEssentials,

  FindAndReplace,

  RemoveFormat,

  Undo,

  SourceEditing,

  GeneralHtmlSupport,

  PageBreak,

  ShowBlocks,

  SelectAll,

  PasteFromOffice
];


// ======================================================
// DEFAULT CONFIG
// ======================================================
CustomClassicEditor.defaultConfig = {

  licenseKey: "GPL",

  toolbar: {
    items: [

      "sourceEditing",
      "|",

      "heading",
      "|",

      "fontFamily",
      "fontSize",
      "fontColor",
      "fontBackgroundColor",
      "|",

      "bold",
      "italic",
      "underline",
      "strikethrough",
      "subscript",
      "superscript",
      "code",
      "removeFormat",

      "|",

      "alignment",

      "|",

      "bulletedList",
      "numberedList",

      "|",

      "outdent",
      "indent",

      "|",

      "link",
      "insertTable",
      "blockQuote",
      "horizontalLine",
      "pageBreak",
      "mediaEmbed",
      "specialCharacters",

      "|",

      "findAndReplace",

      "|",

      "selectAll",

      "|",

      "undo",
      "redo",

      "|",

      "emptyLine"
    ],

    shouldNotGroupWhenFull: true
  },

  list: {
    properties: {
      styles: true,
      startIndex: true,
      reversed: true
    }
  },

  table: {
    contentToolbar: [
      "tableColumn",
      "tableRow",
      "mergeTableCells",
      "tableProperties",
      "tableCellProperties"
    ]
  },

  indentBlock: {
    offset: 40,
    unit: "px"
  },

  lineHeight: {
    options: [
      "1",
      "1.15",
      "1.5",
      "2",
      "2.5",
      "3"
    ]
  },

  fontSize: {
    options: [
      8,
      9,
      10,
      11,
      12,
      14,
      16,
      18,
      20,
      22,
      24,
      26,
      28,
      32,
      36,
      40,
      48,
      56,
      64,
      72
    ],
    supportAllValues: true
  },

  fontColor: {
    supportAllValues: true,
    columns: 10
  },

  fontBackgroundColor: {
    supportAllValues: true,
    columns: 10
  },

  htmlSupport: {
    allow: [
      {
        name: /.*/,
        attributes: true,
        classes: true,
        styles: true
      }
    ],
    disallow: [
      {
        name: /.*/,
        styles: {
          'font-style': /normal/
        }
      }
    ]
  }
};


// ======================================================
// OVERRIDE getData() and setData()
// convert <span data-empty-line="true"></span> <-> [empty_line]
// ======================================================

const originalCreate = CustomClassicEditor.create;

CustomClassicEditor.create = function (...args) {
  return originalCreate.apply(this, args).then((editor) => {

    const originalGetData = editor.getData.bind(editor);

    editor.getData = function (...params) {
      let data = originalGetData(...params);

      data = data.replace(
        /<div data-empty-line="true"><\/div>/g,
        "[empty_line]"
      );

      return data.trim();
    };

    const originalSetData = editor.setData.bind(editor);

    editor.setData = function (data) {
      if (typeof data === "string") {
        data = data.replace(
          /\[empty_line\]/g,
          '<div data-empty-line="true"></div>'
        );
      }

      return originalSetData(data);
    };

    return editor;
  });
};

export default CustomClassicEditor;